import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useAdmin } from '../../context/AdminContext';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faCopy, faTable, faUsers, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { updateTable } from '../../utils/tableService';
import { generateQRCodeUrl } from '../../config';
import { useVenueId } from '../../hooks/useUserData';
import AdminFormModal from '../shared/AdminFormModal';
import AdminNotice from '../shared/AdminNotice';
import { normalizeTableServiceState, TABLE_SERVICE_STATE_OPTIONS } from '../../utils/tableDisplayState';

const EditTableModal = ({ show, onClose, table }) => {
  const { floors, setTables } = useAdmin();
  const { getAccessTokenSilently } = useAuth0();
  const venueId = useVenueId();
  const [formData, setFormData] = useState({ name: '', capacity: '', floorId: '', status: 'active' });
  const [loading, setLoading] = useState(false);
  const [copyNotice, setCopyNotice] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (table) {
      setFormData({
        name: table.name || '',
        capacity: table.capacity || '',
        floorId: table.floorId || '',
        status: normalizeTableServiceState(table.status),
      });
      setSubmitError('');
    }
  }, [table]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSubmitError('');
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleCopyQRCode = async () => {
    if (!table?.qrCode) {
      return;
    }

    await navigator.clipboard.writeText(generateQRCodeUrl(table.qrCode));
    setCopyNotice(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSubmitError('');

    const updatedTable = {
      ...table,
      name: formData.name,
      capacity: parseInt(formData.capacity, 10) || 0,
      floorId: parseInt(formData.floorId, 10),
      status: formData.status,
    };

    try {
      await updateTable(venueId, updatedTable, getAccessTokenSilently);
      setTables((previousTables) => previousTables.map((entry) => (entry.id === table.id ? updatedTable : entry)));
      onClose();
    } catch (error) {
      if (error?.code === 'TABLE_HAS_ACTIVE_SERVICE') {
        setSubmitError('Bu masada aktif ve odenmemis siparisler varken servis durumu kapatilamaz.');
      } else {
        setSubmitError(error?.message || 'Masa guncellenirken bir hata olustu.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminFormModal
      show={show}
      onClose={onClose}
      title="Masa Düzenle"
      subtitle="Masa bilgisi, kapasite ve bağlı kat atamasını tek panelden yönetin."
      icon={faTable}
      notice={
        copyNotice ? (
          <AdminNotice
            tone="success"
            title="QR bağlantısı kopyalandı"
            message="Masa QR bağlantısı panoya kopyalandı."
            onDismiss={() => setCopyNotice(false)}
            autoDismissMs={4000}
          />
        ) : submitError ? (
          <AdminNotice
            tone="danger"
            title="Masa güncellenemedi"
            message={submitError}
            onDismiss={() => setSubmitError('')}
          />
        ) : null
      }
      footer={(
        <>
          <div className="admin-modal-footer-secondary">
            <Button variant="secondary" onClick={onClose} disabled={loading} className="modal-cancel-btn">
              İptal
            </Button>
          </div>
          <div className="admin-modal-footer-primary">
            <Button variant="primary" onClick={handleSubmit} disabled={loading} className="modal-save-btn modal-save-btn-success">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </>
      )}
    >
      <Form onSubmit={handleSubmit} className="admin-form-section">
        <div className="admin-form-block">
          <div className="admin-form-block-title">
            <FontAwesomeIcon icon={faQrcode} />
            QR Bilgisi
          </div>
          {table?.qrCode ? (
            <div className="edit-table-qr-layout">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(generateQRCodeUrl(table.qrCode))}`}
                alt="QR Code"
                className="admin-detail-image edit-table-qr-image"
              />
              <div className="edit-table-qr-meta">
                <div className="admin-detail-label">Bağlantı</div>
                <div className="edit-table-qr-url">{generateQRCodeUrl(table.qrCode)}</div>
                <Button variant="secondary" onClick={handleCopyQRCode} className="modal-neutral-btn edit-table-copy-btn" disabled={!table?.qrCode}>
                  <FontAwesomeIcon icon={faCopy} />
                  QR Bağlantısını Kopyala
                </Button>
              </div>
            </div>
          ) : (
            <div className="admin-detail-placeholder">
              <FontAwesomeIcon icon={faQrcode} size="2x" />
              <span>QR kod bulunamadı.</span>
            </div>
          )}
        </div>

        <div className="admin-form-block">
          <div className="admin-form-block-title">
            <FontAwesomeIcon icon={faTable} />
            Masa Bilgisi
          </div>
          <div className="admin-form-grid">
            <div className="admin-modal-field">
              <Form.Label>Masa Adı</Form.Label>
              <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} className="form-input" required />
            </div>
            <div className="admin-modal-field">
              <Form.Label>
                <FontAwesomeIcon icon={faUsers} className="form-icon" />
                Kapasite
              </Form.Label>
              <Form.Control type="number" name="capacity" min="1" max="20" value={formData.capacity} onChange={handleInputChange} className="form-input" />
            </div>
            <div className="admin-modal-field">
              <Form.Label>
                <FontAwesomeIcon icon={faBuilding} className="form-icon" />
                Kat
              </Form.Label>
              <Form.Select name="floorId" value={formData.floorId} onChange={handleInputChange} className="form-input" required>
                <option value="">Kat seçin</option>
                {floors.map((floor) => (
                  <option key={floor.id} value={floor.id}>
                    {floor.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="admin-modal-field">
              <Form.Label>Servis Durumu</Form.Label>
              <Form.Select name="status" value={formData.status} onChange={handleInputChange} className="form-input" required>
                {TABLE_SERVICE_STATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </div>
      </Form>
    </AdminFormModal>
  );
};

export default EditTableModal;
